import { ComponentPropsWithoutRef } from 'react';
import { ButtonProps } from '@givebutter/primitives/button';
import { RecommendedActionButtonStrategy } from 'components/recommended-actions/RecommendedActionButtonStrategy';
import { RecommendedActionCompletionAnimation } from 'components/recommended-actions/RecommendedActionCompletionAnimation';
import {
    IRecommendedActionContext,
    RecommendedActionProvider,
    useRecommendedActionContext,
} from 'components/recommended-actions/RecommendedActionContext';
import { useRecommendedActionsContext } from 'components/recommended-actions/RecommendedActionsContext';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { twMerge } from 'tailwind-merge';

interface RecommendedActionRootProps
    extends Omit<ButtonProps, 'variant'>,
        IRecommendedActionContext {}
const RecommendedActionRoot = ({
    children,
    className,
    variant,
    action,
    ...rest
}: RecommendedActionRootProps) => {
    const { completableRecommendedActionsKeys: completableKeys } = useAccountLocalStorage(
        ({ completableRecommendedActionsKeys }) => ({ completableRecommendedActionsKeys }),
    );

    return (
        <RecommendedActionProvider variant={variant} action={action}>
            <li className="h-full">
                <RecommendedActionButtonStrategy
                    className={twMerge(
                        'flex w-full justify-start gap-6 text-wrap rounded-xl border border-gray-200 bg-white p-6 text-start hover:border-gray-300 hover:shadow-md focus:border-gray-300 disabled:pointer-events-none disabled:opacity-100',
                        className,
                    )}
                    disabled={completableKeys.includes(action.actionKey)}
                    {...rest}
                >
                    {children}
                </RecommendedActionButtonStrategy>
            </li>
        </RecommendedActionProvider>
    );
};

interface RecommendedActionIconContainerProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}
const RecommendedActionIconContainer = ({
    className,
    ...rest
}: RecommendedActionIconContainerProps) => {
    const {
        completableRecommendedActionsKeys: completableKeys,
        hideRecommendedActionsKey: hideKey,
    } = useAccountLocalStorage(
        ({ completableRecommendedActionsKeys, hideRecommendedActionsKey }) => ({
            completableRecommendedActionsKeys,
            hideRecommendedActionsKey,
        }),
    );
    const { action } = useRecommendedActionContext();
    const { color, actionKey, icon } = action;
    const { containerWasInFrame } = useRecommendedActionsContext();

    return (
        <div
            aria-hidden
            className={twMerge(
                'flex h-full w-full items-center justify-center overflow-hidden rounded-xl',
                color === 'yellow' && 'bg-brand-butter-light',
                color === 'green' && 'bg-brand-jade-light',
                color === 'purple' && 'bg-brand-violet-light',
                color === 'blue' && 'bg-brand-cobalt-light',
                className,
            )}
            {...rest}
        >
            <img src={icon} alt="" className={'size-12'} />
            {containerWasInFrame && completableKeys.includes(actionKey) && (
                <RecommendedActionCompletionAnimation onComplete={() => hideKey(actionKey)} />
            )}
        </div>
    );
};

interface RecommendedActionDurationProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}
const RecommendedActionDuration = ({ className, ...rest }: RecommendedActionDurationProps) => {
    const { action, variant } = useRecommendedActionContext();
    const { duration } = action;

    return (
        <div
            className={twMerge(
                'inline-flex h-fit shrink-0 items-center gap-2 rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700',
                className,
            )}
            {...rest}
        >
            {variant === 'primary' ? (
                <>
                    <span className="text-nowrap rounded-full bg-white px-2 py-1">{duration}</span>{' '}
                    Recommended
                </>
            ) : (
                <>{duration}</>
            )}
        </div>
    );
};

interface RecommendedActionTitleProps extends ComponentPropsWithoutRef<'p'> {}
const RecommendedActionTitle = ({ children, className, ...rest }: RecommendedActionTitleProps) => {
    const { action } = useRecommendedActionContext();
    const { title } = action;

    return (
        <p
            className={twMerge(
                'flex items-center gap-1 text-sm font-medium text-gray-900',
                className,
            )}
            {...rest}
        >
            {title}
            {children}
        </p>
    );
};

interface RecommendedActionDescriptionProps
    extends Omit<ComponentPropsWithoutRef<'p'>, 'children'> {}
const RecommendedActionDescription = ({
    className,
    ...rest
}: RecommendedActionDescriptionProps) => {
    const { action } = useRecommendedActionContext();
    const { description } = action;

    return (
        <p className={twMerge('max-w-2xl text-sm font-normal text-gray-600', className)} {...rest}>
            {description}
        </p>
    );
};

const Root = RecommendedActionRoot;
const IconContainer = RecommendedActionIconContainer;
const Duration = RecommendedActionDuration;
const Title = RecommendedActionTitle;
const Description = RecommendedActionDescription;

export {
    RecommendedActionRoot,
    RecommendedActionIconContainer,
    RecommendedActionDuration,
    RecommendedActionTitle,
    RecommendedActionDescription,
    //
    Root,
    IconContainer,
    Duration,
    Title,
    Description,
};
